import { Controller } from "@hotwired/stimulus"
import {
  initTipTap,
  removeTipTap,
  syncTipTap,
  flushTipTapUploads,
  hasPendingTipTapUploads,
} from "../modules/email/email-tiptap.js"

// Connects to data-controller="tiptap" on a <textarea>
//
// Submit contract with Turbo Drive:
// - TipTap already syncs HTML into the textarea on every update.
// - Only intercept submit when image uploads are still pending.
// - Never stopPropagation — Turbo listens on document and skips when
//   defaultPrevented; stopping propagation left the form dead after a
//   failed/racy requestSubmit() re-entry.
export default class extends Controller {
  static values = {
    uploadUrl: { type: String, default: "/mailboxes/signature_upload" },
    placeholder: { type: String, default: "" },
    height: { type: Number, default: 280 },
  }

  connect() {
    if (!this.element.id) {
      this.element.id = `tiptap_${Math.random().toString(36).slice(2, 10)}`
    }

    this._allowNativeSubmit = false
    this._onFormSubmit = this.handleFormSubmit.bind(this)
    this.element.form?.addEventListener("submit", this._onFormSubmit)

    initTipTap(this.element, {
      height: this.heightValue,
      placeholder: this.placeholderValue || undefined,
      initialContent: this.element.value,
      imagesUploadUrl: this.uploadUrlValue,
      onImageUploadError: () => "Kép feltöltése sikertelen",
    })
  }

  handleFormSubmit(event) {
    if (this._allowNativeSubmit) return

    // Keep textarea in sync for the common path (no pending uploads).
    try {
      syncTipTap(this.element.id)
    } catch (err) {
      console.error("[tiptap] sync before submit failed", err)
    }

    if (!hasPendingTipTapUploads(this.element.id)) {
      // Let Turbo / native submit proceed — do not preventDefault.
      return
    }

    event.preventDefault()
    // Intentionally no stopPropagation — Turbo's submitBubbled checks
    // defaultPrevented and skips; stopping propagation broke re-submit.

    const form = this.element.form
    if (!form) return

    const submitter = event.submitter || undefined

    flushTipTapUploads(this.element.id)
      .then(() => {
        syncTipTap(this.element.id)
        this._resubmit(form, submitter)
      })
      .catch((err) => {
        console.error("[tiptap] flush before submit failed", err)
        // Still attempt submit with whatever content is in the textarea.
        this._resubmit(form, submitter)
      })
  }

  _resubmit(form, submitter) {
    this._allowNativeSubmit = true
    try {
      if (typeof form.requestSubmit === "function") {
        form.requestSubmit(submitter)
      } else {
        HTMLFormElement.prototype.submit.call(form)
      }
    } finally {
      queueMicrotask(() => { this._allowNativeSubmit = false })
    }
  }

  disconnect() {
    this.element.form?.removeEventListener("submit", this._onFormSubmit)
    removeTipTap(this.element)
  }
}
